import { useState, useCallback } from "react";
import { toast } from "sonner";
import { logError } from "@/lib/logger";
import { PDF_MARGIN, saveWithChrome, printChromeStyles, appendPrintChrome } from "@/lib/pdfChrome";

// Generic PDF export + browser Print for an off-screen print node.
// `getHasData` returns whether there's anything to export; `filenameBase` names
// the downloaded PDF. Returns async exportPdf/printNode so callers can await.
export function useNodeExport(printRef, getHasData, filenameBase) {
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);

  const exportPdf = useCallback(async () => {
    if (!getHasData()) {
      toast.error("Nothing to export yet.");
      return;
    }
    setExporting(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await new Promise((r) => setTimeout(r, 400));
      const opt = {
        margin: PDF_MARGIN,
        filename: `${filenameBase}-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", scrollX: 0, scrollY: 0, windowWidth: document.documentElement.offsetWidth },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };
      await saveWithChrome(html2pdf().set(opt).from(printRef.current));
      toast.success("PDF exported.");
    } catch (e) {
      logError("PDF export failed:", e);
      toast.error("PDF export failed.");
    } finally {
      setExporting(false);
    }
  }, [printRef, getHasData, filenameBase]);

  const printNode = useCallback(async () => {
    if (!getHasData()) {
      toast.error("Nothing to print yet.");
      return;
    }
    setPrinting(true);
    try {
      await new Promise((r) => setTimeout(r, 300));
      const el = printRef.current;
      if (!el) throw new Error("Nothing to print");
      const clone = el.cloneNode(true);
      clone.style.position = "static";
      clone.style.left = "0";
      clone.style.top = "0";
      clone.style.width = "100%";
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(
        `<!DOCTYPE html><html><head><title>SalesTap</title><meta charset="utf-8" />` +
          `<style>${printChromeStyles()}</style>` +
          `</head><body></body></html>`
      );
      doc.close();
      doc.body.appendChild(doc.importNode(clone, true));
      appendPrintChrome(doc);
      await new Promise((r) => setTimeout(r, 350));
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
      toast.success("Opening print dialog…");
    } catch (e) {
      logError("Print failed:", e);
      toast.error("Print failed.");
    } finally {
      setPrinting(false);
    }
  }, [printRef, getHasData]);

  return { exporting, printing, exportPdf, printNode };
}
